import { nativeEvents } from '@rocketlane/rocket-llm';
import { AxiosRequestConfig } from 'axios';
import {
  PolicyConflictError,
  PolicyConflictErrorData,
  PolicyConflictItemResponseUnit,
  PolicyConflictResponse,
  PolicyRule,
  ProjectPolicySection,
} from './types';

export const convertPolicyResponseToSection = (
  policy: PolicyConflictResponse,
  violationId: number,
): ProjectPolicySection => {
  const result = policy.reduce((acc, item) => {
    if (!item.isViolated) {
      return acc;
    }

    const children: PolicyRule[] =
      item.rules?.filter((rule) => rule.isViolated) ?? [];

    if (children.length > 0) {
      acc.push({
        ...item,
        rules: children,
      });
    }

    return acc;
  }, [] as PolicyConflictItemResponseUnit[]);

  return { policy: result, violationId };
};

/**
 * Handles 422 policy conflict errors by showing the policy conflict modal.
 * Extracts projectId, ruleResults, and violationId from the error response.
 * Also captures the original request config so the request can be retried
 * after the user resolves the conflicts.
 *
 * This is used in the global axios interceptor to handle policy conflicts for all API calls.
 *
 * @param err - The error object from API response
 */
export const handlePolicyConflictError = (err: unknown) => {
  const error = err as PolicyConflictError;

  const data = error.response?.data;

  if (!data || typeof data !== 'object') {
    return;
  }

  const d = data as PolicyConflictErrorData;
  const { ruleResults, executionId, projectId: rawProjectId } = d;

  if (!Array.isArray(ruleResults) || ruleResults.length === 0) {
    return;
  }

  if (rawProjectId == null) {
    return;
  }

  const projectId =
    typeof rawProjectId === 'number'
      ? rawProjectId
      : Number.parseInt(String(rawProjectId), 10);

  if (!Number.isFinite(projectId)) {
    return;
  }

  const originalRequestConfig =
    (err as { config?: AxiosRequestConfig })?.config ?? null;

  nativeEvents.publish('policyConflictModel', {
    show: true,
    policy: convertPolicyResponseToSection(ruleResults, executionId ?? 0),
    projectId,
    executionId,
    errorData: d,
    originalRequestConfig,
  });
};
